// Close share popup on outside click or scroll

// src/components/CardComponent.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../trending.css";
import { fetchCurrentAffairById } from "../services/trendingService";
import bookmarkImg from "../assets/images/bookmark.svg";
import source from "../assets/images/Source.svg";
import share from "../assets/images/share.svg";

import { parse } from "marked";
// import { parse } from "marked";

// Helper function to strip HTML tags and get plain text
const stripHtmlTags = (html) => {
  if (!html) return "";
  // Create a temporary div element to parse HTML
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;
  // Get text content (strips all HTML tags)
  return tempDiv.textContent || tempDiv.innerText || "";
};

// Helper function to get responsive character limit based on screen width
const getResponsiveCharLimit = () => {
  const width = window.innerWidth;
  if (width < 768) return 100; // Mobile
  if (width < 1024) return 100; // Tablet
  if (width < 1440) return 100; // Desktop
  return 250; // Large desktop
};

// Broadcast so only one share popup can be open at a time.
// Cards ignore the event when they are the one opening (exceptId).
const SHARE_POPUP_CLOSE_EVENT = "currentAffairs:closeSharePopups";
const closeOtherSharePopups = (exceptId) => {
  window.dispatchEvent(
    new CustomEvent(SHARE_POPUP_CLOSE_EVENT, {
      detail: { exceptId: String(exceptId ?? "") },
    })
  );
};

function CurrentAffairsLandipageCards({
  news,
  setIsLoading,
  loadedArticles,
  loadedPage,
  loadedSkip,
  loadedHasMore,
  searchTerm = "",
  allArticles = null,
}) {
  const [charLimit, setCharLimit] = useState(getResponsiveCharLimit());
  const [showSharePopup, setShowSharePopup] = useState(false);
  const [copied, setCopied] = useState(false);
  const [sharePopupPos, setSharePopupPos] = useState({ top: 0, left: 0 });
  const shareUrl = `${window.location.origin}/currentaffairs/${news.id}`;
  const shareId = String(news.id ?? "");

  // Close this card's popup whenever another card opens its own popup
  useEffect(() => {
    const handleGlobalClose = (e) => {
      if (e.detail?.exceptId !== shareId) {
        setShowSharePopup(false);
      }
    };
    window.addEventListener(SHARE_POPUP_CLOSE_EVENT, handleGlobalClose);
    return () =>
      window.removeEventListener(SHARE_POPUP_CLOSE_EVENT, handleGlobalClose);
  }, [shareId]);

  useEffect(() => {
    if (!showSharePopup) return;

    const handleClose = () => setShowSharePopup(false);

    // Close on any outside interaction (mousedown fires before click, so it
    // also works when other elements call stopPropagation on click).
    const handleOutsidePointerDown = (event) => {
      const target = event.target;
      if (
        target &&
        target.closest &&
        (target.closest(".share-popup") || target.closest(".share-icon"))
      ) {
        return;
      }
      setShowSharePopup(false);
    };

    const affairsDiv = document.querySelector(".current-affairsdiv");
    if (affairsDiv) {
      affairsDiv.addEventListener("scroll", handleClose);
    }
    document.addEventListener("mousedown", handleOutsidePointerDown);
    document.addEventListener("touchstart", handleOutsidePointerDown);

    return () => {
      if (affairsDiv) {
        affairsDiv.removeEventListener("scroll", handleClose);
      }
      document.removeEventListener("mousedown", handleOutsidePointerDown);
      document.removeEventListener("touchstart", handleOutsidePointerDown);
    };
  }, [showSharePopup]);

  useEffect(() => {
    const handleResize = () => {
      setCharLimit(getResponsiveCharLimit());
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const calculateReadTime = (text) => {
    if (!text) return "1 min";
    const wordsPerMinute = 200;
    const words = text.split(/\s+/).length;
    const minutes = Math.ceil(words / wordsPerMinute);
    return `${minutes} min`;
  };

  const readTime = news.highlights?.[0]?.highlight_text
    ? calculateReadTime(news.highlights[0].highlight_text)
    : "1 min";

  // console.log("news", news);
  const navigate = useNavigate();

  const handleClick = async (e) => {
     if (news.id && (!e.ctrlKey && !e.shiftKey)) {
      const affairsContainer = document.querySelector(".current-affairsdiv");
      const card = e.currentTarget;
      const containerRect = affairsContainer?.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();

      sessionStorage.setItem(
        "currentAffairs_pendingRestore",
        JSON.stringify({
          articleId: String(news.id),
          relativeTop: containerRect ? cardRect.top - containerRect.top : 0,
          scrollTop: affairsContainer?.scrollTop || 0,
          articles: loadedArticles,
          page: loadedPage,
          skip: loadedSkip,
          hasMore: loadedHasMore,
          listDate: new Date().toDateString(),
          searchTerm,
          allArticles,
        })
      );
      setIsLoading(true);
     }
    try {
      // Replace with dynamic value if needed

      // const data = await fetchCurrentAffairById(news.id);
      // console.log("Current Affair Data:", data);
      // You can set this to state or display it as needed
    } catch (error) {
      console.error("Error:", error);
      alert("Failed to fetch current affair");
    } finally {
      // setIsLoading();
    }

    if (news.id && (!e.ctrlKey && !e.shiftKey)) {
      navigate(`/currentaffairs/${news.id}`);
      // navigate('/CurrentAffairs', { state: { id: news.id } });
    }
  };

  const handleAltClick = () => {
    if (news.source) {
      navigate(`/currentaffairs/${news.id}`);
    }
  };

  const handleSourceClick = (source) => {
    if (source) {
      const url = source.startsWith("http") ? source : `https://${source}`;
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      alert("Failed to copy link");
    }
  };

  return (
     <div>
      <div
        onClick={handleClick}
        className="currentaffairscard-anchortag"
        data-current-affair-id={news.id}
      >    <div className="news-card">
      {news.images && news.images.length > 0 && news.images[0].image_url ? (
        <>
          {news.is_read ? (<div className="news-image-read"></div>) : (<div className="news-image-unread">
          </div>)}
          <img
            src={news.images[0].image_url.split("\n")[0]}
            alt={news.topic}
            className="news-image"
            onError={(e) => {
              e.target.onerror = null; // prevent looping
              e.target.src = "assets/images/noimage.jpeg"; // path to your fallback image
            }}
          />
          <div className="news-content-img">
            <p className="news-title-current-card">
              {news.topic ? news.topic.substring(0, 50) + "..." : ""}
            </p>
            <p className="news-date">
              {" "}
              {news?.ca_created_date &&
                new Date(news.ca_created_date)
                  .toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })
                  .replace(/ /g, "-")}
            </p>
            <p className="news-text">
              {news.content
                ? news.content.split(" ").slice(0, 150).join(" ") + "..."
                : ""}
            </p>
            <div className="card-footer-img">
              {/* <span className="read-time">{readTime} read</span>  */}
              {/* <span className="view-count">{news.view_count || 0} views</span>  */}
              <span className="read-time">
                read {news.view_count || 0} times{" "}
              </span>
              <div className="bookmark-icon-container">
                <img
                  src={`https://www.google.com/s2/favicons?sz=64&domain_url=${news.source}`}
                  alt="source"
                  className="source-icon"
                  title="View Source"
                  onClick={(e) => {
                     e.preventDefault();
                    e.stopPropagation();
                    handleSourceClick(news.source);
                  }}
                />
                <img
                  src={share}
                  alt="share"
                  className="icon-button share-icon"
                  title="Share"
                  onClick={(e) => {
                     e.preventDefault();
                    e.stopPropagation();
                    const rect = e.target.getBoundingClientRect();
                    setSharePopupPos({
                      top: rect.bottom + window.scrollY,
                      left: rect.left + window.scrollX,
                    });
                    closeOtherSharePopups(shareId);
                    setShowSharePopup(prev => !prev);
                    setCopied(false);
                  }}
                />
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          <img
            src={"/assets/images/noimage.jpeg"}
            alt={news.topic}
            className="news-image"
          />
          <div className="news-content-img">
            <p className="news-title-current-card">
              {news.topic ? news.topic.substring(0, 100) + "..." : ""}
            </p>
            <p className="news-date">
              {" "}
              {news?.ca_created_date &&
                new Date(news.ca_created_date)
                  .toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })
                  .replace(/ /g, "-")}
            </p>
            <p className="news-text">
              {news.content
                ? news.content.split(" ").slice(0, 150).join(" ") + "..."
                : ""}
            </p>
            <div className="card-footer-img">
              {/* <span className="read-time">{readTime} read</span>  */}
              {/* <span className="view-count">{news.view_count || 0} views</span>  */}
              <span className="read-time">
                read {news.view_count || 0} times{" "}
              </span>
              <div className="bookmark-icon-container">
                <img
                  src={`https://www.google.com/s2/favicons?sz=64&domain_url=${news.source}`}
                  alt="source"
                  className="source-icon"
                  title="View Source"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSourceClick(news.source);
                  }}
                />
                <img
                  src={share}
                  alt="share"
                  className="icon-button share-icon"
                  title="Share"
                   onClick={(e) => {
                    e.stopPropagation();
                    const rect = e.target.getBoundingClientRect();
                    setSharePopupPos({
                      top: rect.bottom + window.scrollY,
                      left: rect.left + window.scrollX,
                    });
                    closeOtherSharePopups(shareId);
                    setShowSharePopup(prev => !prev);
                    setCopied(false);
                  }}
                />
              </div>
              {/* <img src={bookmarkImg} alt="bookmark" className="bookmark-icon" /> */}
            </div>
          </div>
        </>
      )}

      {/* Share Popup */}
   
    </div></div>

       {showSharePopup && (
        <div
          className="share-popup"
          style={{
            position: "absolute",
            top: sharePopupPos.top,
            left: sharePopupPos.left,
            zIndex: 9999,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* <p><strong>Share this article</strong></p> */}
          {/* <input type="text" value={shareUrl} readOnly /> */}

          <div className="share-buttons-social">
            {/* Copy Button */}
            <div onClick={handleCopy}>
              <img
                src="/assets/images/copy-1.svg"
                alt="Copy link"
                width={30}
                height={30}
              />
            </div>

            {/* WhatsApp */}
            <div
              onClick={() => {
                const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareUrl)}`;
                window.open(whatsappUrl, "_blank");
              }}
            >
              <img
                src="/assets/images/whatsapp.svg"
                alt="Share on WhatsApp"
                width={30}
                height={30}
              />
            </div>

            {/* Telegram */}
            <div
              onClick={() => {
                const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(
                  shareUrl
                )}`;
                window.open(telegramUrl, "_blank");
              }}
            >
              <img
                src="/assets/images/telegram.svg"
                alt="Share on Telegram"
                width={30}
                height={30}
              />
            </div>

            {/* Facebook */}
            <div
              onClick={() => {
                const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
                window.open(facebookUrl, "_blank");
              }}
            >
              <img
                src="/assets/images/facebook.svg"
                alt="Share on Facebook"
                width={30}
                height={30}
              />
            </div>

            {/* LinkedIn */}
            <div
              onClick={() => {
                const linkedInUrl = `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(
                  shareUrl
                )}`;
                window.open(linkedInUrl, "_blank");
              }}
            >
              <img
                src="/assets/images/linkdine.svg"
                alt="Share on LinkedIn"
                width={30}
                height={30}
              />
            </div>

            {/* X (Twitter) */}
            <div
              onClick={() => {
                const twitterUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(
                  shareUrl
                )}`;
                window.open(twitterUrl, "_blank");
              }}
            >
              <img
                src="/assets/images/x.svg"
                alt="Share on X (Twitter)"
                width={30}
                height={30}
              />
            </div>


            {/* Close Button */}
            {/* <div onClick={() => setShowSharePopup(false)} className="clos-icon-pop">
    <img
      src="/assets/images/close.svg"
      alt="Close"
      width={30}
      height={30}
    />
  </div> */}
          </div>

          {copied && <p className="copy-msg">✅ Link copied!</p>}
        </div>
      )}
    </div>
  );
}

export default CurrentAffairsLandipageCards;
